import { DateTime } from "luxon";
import type { NewsletterEdition, NewsletterPublication } from "../api";

// Everything the e-paper component decides, with no React and no DOM, so
// it can be tested as plain functions. Weekdays are cron-style throughout:
// 0 = Sunday … 6 = Saturday. Dates are YYYY-MM-DD strings, which compare
// correctly with < and >.

export const ZONE = "Europe/Zurich";
export const DEFAULT_DAYS = 7;
export const MAX_DAYS = 14;

/** Today's date in Swiss time. Editions are dated in Swiss time, so the visitor's zone must not shift the window. */
export function todayISO(now: DateTime = DateTime.now()): string {
  return now.setZone(ZONE).toISODate();
}

/** The configured window length, or the default when it is absent or nonsensical. */
export function windowLength(days: number | undefined): number {
  if (days === undefined || !Number.isInteger(days) || days < 1 || days > MAX_DAYS) {
    return DEFAULT_DAYS;
  }
  return days;
}

/** The `days` dates ending on `anchor`, oldest first. */
export function windowDates(anchor: string, days: number): string[] {
  const end = DateTime.fromISO(anchor, { zone: ZONE });
  const out: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    out.push(end.minus({ days: i }).toISODate());
  }
  return out;
}

/** Move the anchor by `byDays` (negative = back), never past today. */
export function shiftAnchor(anchor: string, byDays: number, today: string): string {
  const next = DateTime.fromISO(anchor, { zone: ZONE }).plus({ days: byDays }).toISODate();
  return next > today ? today : next;
}

/** luxon counts Monday = 1 … Sunday = 7; cron counts Sunday = 0. */
export function weekday(date: string): number {
  return DateTime.fromISO(date, { zone: ZONE }).weekday % 7;
}

export function editionKey(publication: string, date: string): string {
  return publication + "|" + date;
}

export function indexEditions(editions: NewsletterEdition[]): Map<string, NewsletterEdition> {
  const index = new Map<string, NewsletterEdition>();
  for (const e of editions) {
    index.set(editionKey(e.publication, e.date), e);
  }
  return index;
}

export type CellState =
  | { kind: "edition"; edition: NewsletterEdition }
  | { kind: "missing" }
  | { kind: "blank" };

/** What one (newspaper, date) cell shows. Order matters: an edition always wins, the future is never "missing". */
export function cellState(
  publication: NewsletterPublication,
  date: string,
  editions: Map<string, NewsletterEdition>,
  today: string
): CellState {
  const edition = editions.get(editionKey(publication.slug, date));
  if (edition) return { kind: "edition", edition };
  if (date > today) return { kind: "blank" };
  if (publication.publishingDays.includes(weekday(date))) return { kind: "missing" };
  return { kind: "blank" };
}

/** The one-newspaper widget stored s3-path style ids; all but one equal today's slugs. */
const LEGACY_SLUGS: Record<string, string> = { nzzdaily: "nzz" };

export function legacySlug(newsletter: string): string {
  const key = newsletter.toLowerCase();
  return LEGACY_SLUGS[key] ?? key;
}

interface SelectionSpec {
  hidden?: string[];
  newsletter?: string;
}

/**
 * Slugs the editor has hidden. `hidden` is a deny-list so a newspaper added
 * later shows up on its own; a legacy placement without `hidden` hides
 * everything except its one newspaper.
 */
export function hiddenSlugs(spec: SelectionSpec, all: NewsletterPublication[]): Set<string> {
  if (spec.hidden !== undefined) return new Set(spec.hidden);
  if (spec.newsletter) {
    const keep = legacySlug(spec.newsletter);
    return new Set(all.map((p) => p.slug).filter((slug) => slug !== keep));
  }
  return new Set();
}

/** Dailies first, then the Sunday papers, then anything inactive; ties by name. */
export function sortPublications(publications: NewsletterPublication[]): NewsletterPublication[] {
  return [...publications].sort(
    (a, b) =>
      b.publishingDays.length - a.publishingDays.length || a.name.localeCompare(b.name, "de-CH")
  );
}

export function visiblePublications(spec: SelectionSpec, all: NewsletterPublication[]): NewsletterPublication[] {
  const hidden = hiddenSlugs(spec, all);
  return sortPublications(all.filter((p) => !hidden.has(p.slug)));
}

/** The rows the mobile list shows for one day: only newspapers that have something to say about it. */
export function dayRows(
  publications: NewsletterPublication[],
  date: string,
  editions: Map<string, NewsletterEdition>,
  today: string
): { publication: NewsletterPublication; state: CellState }[] {
  return publications
    .map((publication) => ({ publication, state: cellState(publication, date, editions, today) }))
    .filter((row) => row.state.kind !== "blank");
}
